import type { ReactNode } from "react";

import { MemberCard } from "@/components/member-card";
import { useTranslation } from "@/store/locale";

/**
 * The sign-in showcase.
 *
 * Decorative, so the whole panel is hidden from assistive technology — a screen
 * reader gets the heading and nothing else it has to wade through. The motion
 * is slow and ambient, and `prefers-reduced-motion` stops it outright.
 */
export function Showcase({
  visual = <MemberCard />,
  title,
  subtitle,
}: {
  visual?: ReactNode;
  /** Defaults to the sign-in copy; the password screens speak for themselves. */
  title?: ReactNode;
  subtitle?: ReactNode;
}) {
  const t = useTranslation();

  return (
    <section className="relative isolate flex h-full flex-col justify-center overflow-hidden rounded-3xl p-6 sm:p-8">
      {/* Pastel wash: four corners, layered as radial gradients so the seams
          between them stay soft at any aspect ratio. */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[#f2f0f7] bg-[radial-gradient(120%_90%_at_0%_0%,#dff0e2_0%,transparent_55%),radial-gradient(110%_90%_at_100%_0%,#fbf3d8_0%,transparent_55%),radial-gradient(110%_100%_at_100%_100%,#f7dcea_0%,transparent_60%),radial-gradient(120%_110%_at_0%_100%,#d9dbf7_0%,transparent_60%)] dark:opacity-35"
      />

      <div
        aria-hidden
        className="relative flex min-h-40 flex-1 items-center justify-center rounded-2xl border border-white/60 bg-white/45 p-6 backdrop-blur-sm sm:min-h-64 lg:h-[57%] lg:min-h-0 lg:flex-none dark:border-white/10 dark:bg-white/5"
      >
        {/* The wash breathes behind the card; drift is 18s, so it reads as
            ambient rather than as something happening. */}
        <div className="animate-drift absolute inset-0 -z-10 rounded-2xl bg-[radial-gradient(50%_60%_at_30%_40%,rgba(255,255,255,0.7)_0%,transparent_70%)]" />
        {visual}
      </div>

      <div className="mt-6 text-center sm:mt-10">
        <h2 className="text-ink-900 text-title-1 dark:text-fg">
          {title ?? t("showcase.title")}
        </h2>
        <p className="text-ink-600 text-callout dark:text-fg-muted mx-auto mt-3 max-w-sm text-balance">
          {subtitle ?? t("showcase.subtitle")}
        </p>
      </div>
    </section>
  );
}
